import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Instagram } from "lucide-react";
import { houseSaundersImages } from "./HouseSaundersPage";

// Clean signature shots from the other three projects (flagged images removed)
const otherProjectImages = [
  // Prescient Silverwood Office
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/ek5uv03n_Modern%20Prescient%20Lounge%20with%20Warm%20Accents.png",
  // Prescient Fund Services VIP Box
  "https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/cqr3d5x8_PRES%201.jpeg",
  // Standard Bank
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/p0sh9gjf_Modern%20Executive%20Conference%20Room.png",
  // Prescient Silverwood Office
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/p1t9si34_canteen.jpeg",
];

// Hero rotation — every 333B image, interleaved with the other projects
const heroImages = (() => {
  const result = [];
  const maxLen = Math.max(houseSaundersImages.length, otherProjectImages.length);
  for (let i = 0; i < maxLen; i++) {
    if (i < houseSaundersImages.length) result.push(houseSaundersImages[i]);
    if (i < otherProjectImages.length) result.push(otherProjectImages[i]);
  }
  return result;
})();

const services = [
  {
    title: "Corporate Design",
    description: "Professional spaces that inspire productivity and reflect your brand identity.",
  },
  {
    title: "Residential Design",
    description: "Luxurious homes tailored to your lifestyle with meticulous attention to detail.",
  },
  {
    title: "Restaurant & Cafe",
    description: "Memorable hospitality environments that elevate the dining experience.",
  },
];

const features = [
  { number: "11", label: "Years in Business" },
  { number: "21", label: "Years Experience" },
  { number: "200+", label: "Projects Completed" },
];

const whyChooseUs = [
  {
    title: "Personalized Approach",
    description: "Every client receives individual attention. You're never just a number to us.",
  },
  {
    title: "Trusted Network",
    description: "Long-standing partnerships with premium suppliers and skilled craftsmen.",
  },
  {
    title: "Turnkey Solutions",
    description: "From concept to completion, we manage every detail of your project.",
  },
  {
    title: "Proven Track Record",
    description: "Over a decade of success built entirely through client referrals.",
  },
];

const heroLabels = [
  { name: "Corporate", filter: "corporate" },
  { name: "Residential", filter: "residential" },
  { name: "VIP Suites", filter: "vip" },
];

export default function HomePage() {
  const [currentImage, setCurrentImage] = useState(0);
  const [isSliding, setIsSliding] = useState(false);

  // Preload all images on mount to prevent flicker
  useEffect(() => {
    heroImages.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Slide right-to-left every 7s
  useEffect(() => {
    const timer = setInterval(() => {
      setIsSliding(true);
    }, 7000);
    return () => clearInterval(timer);
  }, []);

  // After slide animation completes, snap to next image instantly
  const handleTransitionEnd = () => {
    if (isSliding) {
      setCurrentImage((prev) => (prev + 1) % heroImages.length);
      setIsSliding(false);
    }
  };

  const nextIdx = (currentImage + 1) % heroImages.length;

  return (
    <div data-testid="home-page">
      {/* Hero Section - Full Screen */}
      <section className="relative min-h-screen w-full overflow-hidden flex flex-col" style={{ backgroundColor: '#FAF9F6' }} data-testid="hero-section">
        {/* Image area — full width, auto height on mobile, full screen on desktop */}
        <div className="relative w-full pt-16 md:pt-0 md:absolute md:inset-0">
          <div
            className="relative w-full aspect-[16/10] md:aspect-auto md:h-full flex will-change-transform overflow-hidden"
            style={{
              width: '200%',
              transform: isSliding ? 'translateX(-50%)' : 'translateX(0)',
              transition: isSliding ? 'transform 1.4s cubic-bezier(0.25, 0.1, 0.25, 1)' : 'none',
            }}
            onTransitionEnd={handleTransitionEnd}
          >
            <div className="w-1/2 h-full flex-shrink-0 relative">
              <img
                src={heroImages[currentImage]}
                alt="Interior Design"
                className="w-full h-full object-contain md:object-cover"
                style={{ backgroundColor: '#FAF9F6' }}
              />
            </div>
            <div className="w-1/2 h-full flex-shrink-0 relative">
              <img
                src={heroImages[nextIdx]}
                alt="Interior Design"
                className="w-full h-full object-contain md:object-cover"
                style={{ backgroundColor: '#FAF9F6' }}
              />
            </div>
          </div>
        </div>

        {/* Bottom gradient for text legibility — desktop only */}
        <div className="hidden md:block absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />

        {/* Bottom-Left — Company tagline & CTA */}
        <div className="relative md:absolute md:bottom-12 md:left-16 z-10 px-6 py-8 md:p-0 md:max-w-lg" data-testid="hero-bottom-left">
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-6xl text-black md:text-white leading-[1.1] mb-4 md:drop-shadow-lg">
            Crafting
            <br />
            <span className="italic text-warmBrown md:text-[#D4C5A9]">Exceptional</span>
            {" "}Spaces
          </h1>
          <p className="text-black/60 md:text-white/80 text-sm md:text-base font-body font-light mb-6 max-w-sm leading-relaxed md:drop-shadow-md">
            Boutique interior design for discerning clients.
          </p>
          <div className="flex gap-4">
            <Link
              to="/contact"
              className="bg-black md:bg-white text-white md:text-black hover:bg-warmBrown md:hover:bg-[#D4C5A9] hover:text-white md:hover:text-black transition-all duration-500 uppercase tracking-[0.2em] text-xs px-6 py-3 font-semibold inline-flex items-center gap-2"
              data-testid="hero-cta-primary"
            >
              Start Your Project
              <ArrowRight size={14} />
            </Link>
            <Link
              to="/portfolio"
              className="border border-black/30 md:border-white/60 text-black md:text-white hover:bg-black/5 md:hover:bg-white/10 transition-all duration-500 uppercase tracking-[0.2em] text-xs px-6 py-3 inline-flex items-center gap-2"
              data-testid="hero-cta-secondary"
            >
              View Projects
            </Link>
          </div>
          <a
            href="https://www.instagram.com/nh_interior_design_/"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 text-black/50 md:text-white/60 hover:text-warmBrown md:hover:text-[#D4C5A9] transition-colors duration-300 font-body text-xs uppercase tracking-[0.15em]"
            data-testid="hero-instagram"
          >
            <Instagram size={14} />
            @nh_interior_design_
          </a>
        </div>

        {/* Right side — Bold stacked links */}
        <div className="hidden md:block absolute top-28 md:top-32 right-8 md:right-16 z-10 text-right" data-testid="hero-top-right">
          <div className="flex flex-col gap-2">
            {heroLabels.map((item) => (
              <Link
                key={item.filter}
                to={`/portfolio?filter=${item.filter}`}
                className="font-heading text-lg md:text-xl lg:text-2xl text-white font-bold uppercase tracking-wide drop-shadow-lg hover:text-[#D4C5A9] transition-colors duration-300"
                data-testid={`hero-link-${item.filter}`}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>

        {/* Bottom-Right — Image progress dots */}
        <div className="hidden md:flex absolute bottom-12 md:bottom-16 right-8 md:right-16 z-10 items-center gap-3" data-testid="hero-image-counter">
          {heroImages.map((src, index) => (
            <div
              key={src}
              className={`rounded-full transition-all duration-700 ${
                index === currentImage
                  ? "w-3 h-3 bg-white"
                  : "w-1.5 h-1.5 bg-white/40"
              }`}
            />
          ))}
        </div>
      </section>

      {/* About Preview Section */}
      <section className="section-padding bg-soft-beige" data-testid="about-preview-section">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-warmBrown uppercase tracking-[0.3em] text-xs mb-4 font-body">
                About NH Interiors
              </p>
              <h2 className="font-heading text-3xl md:text-5xl text-black mb-6">
                Where Vision Meets
                <br />
                <span className="italic text-warmBrown">Craftsmanship</span>
              </h2>
              <p className="text-black/60 font-body leading-relaxed mb-6">
                For over a decade, NH Interiors has been transforming spaces
                across Cape Town and beyond. Founded on the principle that great
                design comes from truly understanding our clients, we've built
                our reputation entirely through word-of-mouth referrals.
              </p>
              <p className="text-black/60 font-body leading-relaxed mb-8">
                We believe in personalized service—every project receives our
                complete attention and dedication. Our trusted network of
                suppliers and craftsmen ensures exceptional quality at every stage.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-3 text-warmBrown hover:text-black transition-colors duration-300 font-body text-sm uppercase tracking-wider"
                data-testid="about-preview-link"
              >
                Learn More About Us
                <ArrowRight size={16} />
              </Link>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/ek5uv03n_Modern%20Prescient%20Lounge%20with%20Warm%20Accents.png"
                  alt="NH Interiors Project"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-warmBrown p-8">
                <p className="font-heading text-4xl text-white">11+</p>
                <p className="text-white/80 text-sm font-body uppercase tracking-wider">
                  Years Experience
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-cream" data-testid="services-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-warmBrown uppercase tracking-[0.3em] text-xs mb-4 font-body">
              Our Expertise
            </p>
            <h2 className="font-heading text-3xl md:text-5xl text-black">
              Comprehensive Design
              <br />
              <span className="italic text-warmBrown">Services</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-0 divide-x divide-black/10">
            {services.map((service, index) => (
              <div
                key={service.title}
                className="group px-8 py-6"
                data-testid={`service-card-${index}`}
              >
                <span className="text-warmBrown/40 font-heading text-5xl">0{index + 1}</span>
                <h3 className="font-heading text-2xl text-black mt-4 mb-3">
                  {service.title}
                </h3>
                <p className="text-black/50 font-body leading-relaxed text-sm">
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="btn-secondary inline-flex items-center gap-3"
              data-testid="services-cta"
            >
              View All Services
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-padding bg-cream" data-testid="why-choose-us-section">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <p className="text-warmBrown uppercase tracking-[0.3em] text-xs mb-4 font-body">
                The NH Difference
              </p>
              <h2 className="font-heading text-3xl md:text-5xl text-black mb-6">
                Why Clients
                <br />
                <span className="italic text-warmBrown">Choose Us</span>
              </h2>
              <p className="text-black/60 font-body leading-relaxed mb-8">
                In a world of mass-produced design, we offer something rare:
                genuine partnership. We take the time to understand your vision,
                your needs, and your aspirations.
              </p>
              <div className="flex items-start gap-4">
                <span className="text-warmBrown font-heading text-3xl leading-none">"</span>
                <p className="text-black/80 font-body italic">
                  Our growth has been 100% through referrals. That speaks to
                  the relationships we build.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-x-12 gap-y-10">
              {whyChooseUs.map((item, index) => (
                <div
                  key={item.title}
                  className="border-l-2 border-warmBrown/30 pl-6"
                  data-testid={`why-choose-item-${index}`}
                >
                  <h4 className="font-heading text-lg text-black mb-2">
                    {item.title}
                  </h4>
                  <p className="text-black/50 text-sm font-body leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section
        className="relative py-32 md:py-48"
        style={{
          backgroundImage: `url(https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/q6czf4k2_IMG_2492.webp)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="final-cta-section"
      >
        <div className="absolute inset-0 bg-black/70" />
        <div className="container-custom relative z-10 text-center">
          <p className="text-champagne uppercase tracking-[0.3em] text-xs mb-6 font-body">
            Your Vision, Our Expertise
          </p>
          <h2 className="font-heading text-4xl md:text-6xl lg:text-7xl text-white mb-8">
            Ready to Transform
            <br />
            <span className="italic text-champagne">Your Space?</span>
          </h2>
          <p className="text-white/70 font-body text-lg max-w-xl mx-auto mb-12">
            Let's discuss how we can bring your vision to life. From initial
            concept to final reveal, we're with you every step of the way.
          </p>
          <Link
            to="/contact"
            className="btn-light inline-flex items-center gap-3"
            data-testid="final-cta-button"
          >
            Start Your Project
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
